// This function generates route data based on the choreography file structure
// Since we can't read the filesystem at runtime in a browser, we need to maintain this list
// but it's much cleaner than having routes scattered throughout the codebase

export interface ChoreographyRoute {
  path: string;
  choreoName: string;
  choreoFilePath: string; // Full path to the JSON file
  title: string;
  year: string;
  hidden: boolean; // Hidden routes are not listed on the IndexPage
}

// Function to convert filename to route path
function filenameToPath(filename: string): string {
  return filename.replace(".json", "").replace(/_/g, "-").toLowerCase();
}

// Function to convert filename to display title
function filenameToTitle(filename: string): string {
  return filename
    .replace(".json", "")
    .replace(/_/g, " ")
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

// This would ideally be generated automatically, but for now we maintain it manually
// TODO: Consider using a build-time script to generate this from the actual file structure
export const choreographyFiles = {
  "2023": ["crazy_fire_a_2023.json"],
  "2024": [
    "identity_a_2024.json",
    "identity_b_2024.json"
  ],
  "2025": [
    "identity_a_2025.json",
    "identity_b_2025.json",
    "i_am_yours_c_2025.json",
  ],
  "2026": [
    "one_chance_a_2026.json",
    "crazy_fire_b_2026.json",
    "i_am_yours_c_2026.json",    
  ],
  "2027" : [
    "one_chance_a_2027.json",
    "crazy_fire_b_2027.json",
    "let_me_think_about_it_a_solo_2027.json"
  ]
};

// Routable but not listed on the IndexPage; only reachable via direct link
export const hiddenChoreographyFiles = {
  // "2027": ["let_me_think_about_it_a_solo_2027.json"],
};

function filesToRoutes(
  filesByYear: { [year: string]: string[] },
  hidden: boolean
): ChoreographyRoute[] {
  return Object.entries(filesByYear).flatMap(([year, files]) =>
    files.map((filename) => ({
      path: `/${filenameToPath(filename)}`,
      choreoName: filename,
      choreoFilePath: `/choreographies/${year}/${filename}`,
      title: filenameToTitle(filename),
      year: year,
      hidden: hidden,
    }))
  );
}

// Generate all choreography routes
export function generateChoreographyRoutes(): ChoreographyRoute[] {
  return [
    ...filesToRoutes(choreographyFiles, false),
    ...filesToRoutes(hiddenChoreographyFiles, true),
  ];
}

// Get choreography data organized by year (for IndexPage)
export function getChoreographyDataByYear() {
  const routes = generateChoreographyRoutes();
  const dataByYear: { [year: string]: Array<{ to: string; title: string }> } =
    {};

  routes.forEach((route) => {
    if (route.hidden) {
      return;
    }
    if (!dataByYear[route.year]) {
      dataByYear[route.year] = [];
    }
    dataByYear[route.year].push({
      to: route.path,
      title: route.title,
    });
  });

  return dataByYear;
}
